/**
 *
 * SnackbarProvider
 *
 */

import React, { memo } from 'react';

import clsx from 'clsx';
import { SnackbarProvider as NotistackProvider, useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useInjectReducer } from 'redux-injectors';

import { Mui } from 'components';

import reducer from './ducks';
// Local components
import Notifier from './Notifier';

const useStyles = Mui.makeStyles(theme => {
  const { grey = {}, green = {}, yellow = {}, red = {} } = theme.palette.brand || {};
  const border = color => (color ? `1px solid ${color}` : '1px solid');
  return {
    contentRoot: {
      backgroundColor: grey[200],
      border: border(grey[400]),
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[1],
      color: theme.palette.text.primary,
    },
    message: { color: theme.palette.text.primary },
    variantInfo: { backgroundColor: grey[200], border: border(grey[400]) },
    variantSuccess: { backgroundColor: green[100], border: border(green[200]) },
    variantWarning: { backgroundColor: yellow[100], border: border(yellow[200]) },
    variantError: { backgroundColor: red[100], border: border(red[200]) },
    icon: { fontSize: 20, marginInlineEnd: 8 },
    iconInfo: { color: grey[600] },
    iconSuccess: { color: green[300] },
    iconWarning: { color: yellow[400] },
    iconError: { color: red[400] },
  };
});

const CloseAction = ({ snackBarKey }) => {
  const { closeSnackbar } = useSnackbar();
  return (
    <Mui.IconButton size="small" color="inherit" onClick={() => closeSnackbar(snackBarKey)}>
      <Mui.Icon>close</Mui.Icon>
    </Mui.IconButton>
  );
};

CloseAction.propTypes = {
  snackBarKey: PropTypes.number,
};

export function SnackbarProvider(props) {
  useInjectReducer({ key: 'snackbarProvider', reducer });

  const { children } = props;

  const classes = useStyles();

  return (
    <NotistackProvider
      maxSnack={3}
      anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      action={key => <CloseAction snackBarKey={key} />}
      iconVariant={{
        info: <Mui.Icon className={clsx(classes.icon, classes.iconInfo)}>error_outline</Mui.Icon>,
        success: (
          <Mui.Icon className={clsx(classes.icon, classes.iconSuccess)}>
            check_circle_outline
          </Mui.Icon>
        ),
        warning: <Mui.Icon className={clsx(classes.icon, classes.iconWarning)}>warning</Mui.Icon>,
        error: <Mui.Icon className={clsx(classes.icon, classes.iconError)}>close</Mui.Icon>,
      }}
      classes={{
        contentRoot: classes.contentRoot,
        message: classes.message,
        variantInfo: classes.variantInfo,
        variantWarning: classes.variantWarning,
        variantError: classes.variantError,
        variantSuccess: classes.variantSuccess,
      }}>
      <Notifier />
      {children}
    </NotistackProvider>
  );
}

SnackbarProvider.propTypes = {
  children: PropTypes.any,
};

export default memo(SnackbarProvider);
